import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronRight,
  Flame,
  Inbox,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  AlertDialog,
  Button,
  CashPositionCard,
  EmptyState,
  FullScreenModal,
  ProgressBar,
  useToast,
} from '../../components';
import {
  buildCategoryMap,
  selectRecentTransactions,
  useActions,
  useAppData,
} from '../../data';
import { cn } from '../../lib/cn';
import {
  calculateDailyAllowance,
  calculatePYF,
  calculateZeroBased,
} from '../../lib/budget';
import { computeSafeToSpend } from '../../lib/cashPosition';
import { formatCurrency } from '../../lib/currency';
import {
  formatLongDate,
  timeOfDayGreeting,
  toIsoDate,
} from '../../lib/date';
import { springGentle, springQuick, tapScale } from '../../tokens';
import { EditTransactionSheet } from '../activity/EditTransactionSheet';
import { type EntryType } from './InlineEntry';
import { InsightsStrip } from './InsightsStrip';
import { QuickApplySheet } from './QuickApplySheet';
import { RecentRow } from './RecentRow';
import { TodayFabs } from './TodayFabs';
import { TransactionForm } from './TransactionForm';

type TodayScreenProps = {
  onNavigate?: (
    tab: 'today' | 'activity' | 'plan' | 'insights' | 'settings',
    action?: 'addIncome',
  ) => void;
};

export function TodayScreen({ onNavigate }: TodayScreenProps) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const today = useMemo(() => new Date(), []);

  const [entryOpen, setEntryOpen] = useState<EntryType | null>(null);
  const [entryDirty, setEntryDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [quickApplyOpen, setQuickApplyOpen] = useState(false);

  const categoryMap = useMemo(
    () => buildCategoryMap(data.categories),
    [data.categories],
  );
  const recent = useMemo(() => selectRecentTransactions(data, 6), [data]);
  const daily = useMemo(
    () => calculateDailyAllowance(data, today),
    [data, today],
  );

  const pyfRemaining = useMemo(() => {
    if (data.profile.budgetModel !== 'pyf') return 0;
    const r = calculatePYF(data, today);
    return Math.max(0, r.periodGoal - r.goalToDate);
  }, [data, today]);

  const hasIncomeTransaction = data.transactions.some((t) => t.type === 'income');
  const hasScheduledIncome = data.scheduledItems.some(
    (s) => s.type === 'income' && s.active,
  );
  const isVariableEmpty = !hasIncomeTransaction && !hasScheduledIncome;

  const heroToneClass = isVariableEmpty
    ? 'text-text-tertiary'
    : daily.status === 'over-budget'
      ? 'hero-negative'
      : daily.status === 'spent'
        ? 'text-text-tertiary'
        : 'hero-positive';

  const statusMessage = isVariableEmpty
    ? 'Log a paycheck to set your daily budget'
    : daily.status === 'over-budget'
      ? 'Over budget today'
      : daily.status === 'spent'
        ? 'Budget spent for today'
        : daily.status === 'under-budget'
          ? 'Great day to save'
          : "You're on track";

  const statusToneClass = isVariableEmpty
    ? 'text-accent'
    : daily.status === 'over-budget'
      ? 'text-negative'
      : daily.status === 'spent'
        ? 'text-text-tertiary'
        : daily.status === 'under-budget'
          ? 'text-accent'
          : 'text-positive';

  const periodProgress =
    daily.period.totalDays > 0
      ? daily.period.dayInPeriod / daily.period.totalDays
      : 0;

  const model = data.profile.budgetModel;
  const isSafeToSpendModel =
    model === 'pyf' || model === '503020' || model === 'zero';
  const accounts = data.accounts ?? [];
  const hasAccounts = accounts.length > 0;
  const useSafeToSpendHero = isSafeToSpendModel && hasAccounts;

  const cashPosition = useMemo(
    () =>
      useSafeToSpendHero
        ? computeSafeToSpend(
            accounts,
            data.scheduledItems,
            data.transactions,
            toIsoDate(daily.period.start),
            toIsoDate(daily.period.end),
            pyfRemaining,
          )
        : null,
    [
      useSafeToSpendHero,
      accounts,
      data.scheduledItems,
      data.transactions,
      daily.period.start,
      daily.period.end,
      pyfRemaining,
    ],
  );

  const zeroResult = useMemo(
    () => (model === 'zero' ? calculateZeroBased(data, today) : null),
    [model, data, today],
  );

  const explicitHeroView = data.profile.heroView;
  const derivedHeroView: 'daily' | 'period' = (() => {
    if (isSafeToSpendModel) return 'period';
    if (!hasIncomeTransaction && !hasScheduledIncome) return 'daily';
    const hasSpreadIncome = data.transactions.some(
      (t) =>
        t.type === 'income' && typeof t.spreadDays === 'number' && t.spreadDays > 1,
    );
    if (hasSpreadIncome || hasScheduledIncome) return 'daily';
    return 'period';
  })();
  const heroView: 'daily' | 'period' = explicitHeroView ?? derivedHeroView;

  const rawHeroAmount = (() => {
    if (heroView === 'daily') return daily.availableToday;
    if (useSafeToSpendHero) return cashPosition!.safeToSpend;
    return daily.remainingThisPeriod;
  })();
  const heroAmount = isVariableEmpty ? 0 : rawHeroAmount;
  const formattedHero = formatCurrency(heroAmount, {
    currency: data.profile.currency,
  });
  const heroLabel = (() => {
    if (heroView === 'daily') return 'Available today';
    if (useSafeToSpendHero) return 'Safe to spend';
    return 'Left this period';
  })();
  const showAddAccountsHint =
    isSafeToSpendModel && !hasAccounts && !isVariableEmpty;

  // Materialize any scheduled bills that came due while the app was
  // closed (idempotent via dedupe on scheduled-item id + due date).
  useEffect(() => {
    actions.runScheduledBills();
  }, [actions]);

  const cancelEntry = () => {
    if (entryDirty) {
      setDiscardOpen(true);
      return;
    }
    setEntryOpen(null);
    setEntryDirty(false);
  };

  const confirmDiscard = () => {
    setDiscardOpen(false);
    setEntryOpen(null);
    setEntryDirty(false);
  };

  const saveEntry = (draft: {
    type: EntryType;
    description: string;
    categoryId: string;
    date: string;
    amount: number;
    spreadDays?: number;
  }) => {
    if (draft.amount <= 0) {
      toast.show({ message: 'Enter an amount above $0', kind: 'warning' });
      return;
    }
    if (!draft.categoryId) {
      toast.show({ message: 'Pick a category', kind: 'error' });
      return;
    }
    const category = categoryMap.get(draft.categoryId);
    actions.addTransaction({
      amount: draft.amount,
      type: draft.type,
      categoryId: draft.categoryId,
      description: draft.description || category?.name || draft.type,
      date: draft.date,
      source: 'manual',
      spreadDays: draft.spreadDays,
    });
    setEntryOpen(null);
    toast.show({
      message: `Saved ${formatCurrency(draft.amount, {
        currency: data.profile.currency,
      })}`,
      kind: 'success',
    });
  };

  const presetCount = (data.profile.quickPresets ?? []).length;

  return (
    <main className="mx-auto max-w-content pb-[calc(env(safe-area-inset-bottom)+24px)]">
      {/* Page background mesh — almost imperceptible, follows status */}
      <div
        className={cn(
          'mesh-bg',
          isVariableEmpty
            ? ''
            : daily.status === 'over-budget'
              ? 'mesh-negative-active'
              : daily.status === 'under-budget'
                ? 'mesh-positive-active'
                : '',
        )}
        aria-hidden
      />

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springGentle}
        className="px-5 pt-4 pb-2 flex items-center justify-between"
      >
        <div>
          <p className="text-[13px] font-medium text-text-tertiary">
            {timeOfDayGreeting(today)}
            {data.profile.name ? `, ${data.profile.name}` : ''}
          </p>
          <h1 className="text-[17px] font-semibold text-text-primary inline-flex items-center gap-1.5">
            {formatLongDate(today)}
            {(data.profile.achievements?.streaks.daysLogged.current ?? 0) >=
            3 ? (
              <button
                type="button"
                onClick={() => onNavigate?.('settings')}
                aria-label="View streaks"
                className="inline-flex items-center gap-0.5 text-amber-500/85 text-[13px] font-medium"
              >
                <Flame size={12} strokeWidth={2} />
                {data.profile.achievements?.streaks.daysLogged.current}
              </button>
            ) : null}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => onNavigate?.('settings')}
          aria-label="Settings"
          className="w-11 h-11 inline-flex items-center justify-center text-text-secondary"
        >
          <SettingsIcon size={22} strokeWidth={1.75} />
        </button>
      </motion.header>

      {/* Hero card */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springGentle, delay: 0 }}
        className="mx-5"
      >
        <div className="rounded-3xl bg-surface border border-surface-secondary p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]">
          {isVariableEmpty ? (
            <div className="flex flex-col items-center text-center">
              <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
                Available today
              </div>
              <div className="mt-3 text-hero-sm font-light font-mono tabular-nums text-text-tertiary">
                $0.00
              </div>
              <p className="mt-3 text-[14px] font-medium text-text-secondary max-w-[280px]">
                Add your first paycheck to see your daily budget.
              </p>
              <Button
                variant="primary"
                size="md"
                className="mt-4"
                onClick={() => onNavigate?.('plan', 'addIncome')}
              >
                Add a paycheck
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-tertiary">
                  {heroLabel}
                </div>
                <HeroViewToggle
                  value={heroView}
                  onChange={(next) =>
                    actions.updateProfile({ heroView: next })
                  }
                />
              </div>
              <div className="mt-3 relative h-[58px] overflow-hidden">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={`${heroView}:${formattedHero}`}
                    initial={{ y: 24, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -24, opacity: 0 }}
                    transition={springGentle}
                    className={cn(
                      'absolute inset-0 hero-amount font-mono',
                      heroToneClass,
                    )}
                  >
                    {formattedHero}
                  </motion.div>
                </AnimatePresence>
              </div>
              {showAddAccountsHint ? (
                <button
                  type="button"
                  onClick={() => onNavigate?.('settings')}
                  className="mt-3 text-left text-[13px] font-medium text-accent underline-offset-2 hover:underline"
                >
                  Add your accounts in Settings to see your safe to spend amount
                </button>
              ) : (
                <p
                  className={cn('mt-3 text-[14px] font-medium', statusToneClass)}
                >
                  {statusMessage}
                </p>
              )}
              <div className="mt-6">
                <ProgressBar value={periodProgress} />
                <div className="mt-3 flex items-center justify-between text-[12px] text-text-tertiary">
                  <span>
                    Day {daily.period.dayInPeriod} of {daily.period.totalDays}
                  </span>
                  {model === 'pyf' ? (
                    <span className="font-mono tabular-nums">
                      {`${formatCurrency(pyfRemaining, {
                        currency: data.profile.currency,
                      })} PYF remaining`}
                    </span>
                  ) : model === 'zero' && zeroResult ? (
                    <span className="font-mono tabular-nums">
                      {`${formatCurrency(zeroResult.toAssignPeriod, {
                        currency: data.profile.currency,
                      })} unassigned`}
                    </span>
                  ) : model === '503020' ? null : (
                    <span className="font-mono tabular-nums">
                      {`${formatCurrency(daily.remainingThisPeriod, {
                        currency: data.profile.currency,
                      })} left this period`}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Cash position breakdown — directly below hero */}
      {useSafeToSpendHero ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springGentle, delay: 0.06 }}
          className="mx-5 mt-4"
        >
          <CashPositionCard
            accounts={accounts}
            scheduledItems={data.scheduledItems}
            transactions={data.transactions}
            periodStart={toIsoDate(daily.period.start)}
            periodEnd={toIsoDate(daily.period.end)}
            pyfRemaining={pyfRemaining}
            currency={data.profile.currency}
          />
        </motion.div>
      ) : null}

      {/* Insights strip */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springGentle, delay: 0.12 }}
      >
        <InsightsStrip
          data={data}
          today={today}
          onTap={() => onNavigate?.('insights')}
        />
      </motion.div>

      {/* Recent — flat list, no card wrapper */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springGentle, delay: 0.18 }}
        className="mt-6"
      >
        <div className="px-5 mb-1 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-text-primary">Recent</h2>
          {recent.length > 0 && onNavigate ? (
            <button
              type="button"
              onClick={() => onNavigate('activity')}
              className="text-[13px] font-medium text-accent inline-flex items-center gap-0.5"
            >
              See all
              <ChevronRight size={14} strokeWidth={1.75} />
            </button>
          ) : null}
        </div>
        {recent.length === 0 ? (
          <div className="mx-5">
            <EmptyState
              icon={<Inbox size={36} strokeWidth={1.5} />}
              title="No transactions yet"
              description="Tap the + button to add your first one."
            />
          </div>
        ) : (
          <div>
            {recent.map((t, i) => (
              <div
                key={t.id}
                className={
                  i < recent.length - 1
                    ? 'border-b border-surface-secondary/60'
                    : ''
                }
              >
                <RecentRow
                  transaction={t}
                  category={categoryMap.get(t.categoryId)}
                  currency={data.profile.currency}
                  now={today}
                  onClick={() => setEditingId(t.id)}
                />
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Floating FABs */}
      <TodayFabs
        onQuickAdd={() => {
          if (presetCount > 0) setQuickApplyOpen(true);
          else setEntryOpen('expense');
        }}
        onAddIncome={() => setEntryOpen('income')}
        onAddExpense={() => setEntryOpen('expense')}
      />

      <QuickApplySheet
        open={quickApplyOpen}
        onClose={() => setQuickApplyOpen(false)}
      />

      <FullScreenModal
        open={entryOpen !== null}
        onClose={cancelEntry}
        title={entryOpen === 'income' ? 'Add income' : 'Add expense'}
      >
        {entryOpen ? (
          <TransactionForm
            type={entryOpen}
            doneLabel="Save"
            onDirtyChange={setEntryDirty}
            onSave={(draft) => {
              saveEntry(draft);
              setEntryDirty(false);
            }}
          />
        ) : null}
      </FullScreenModal>

      <AlertDialog
        open={discardOpen}
        title="Discard transaction?"
        message="You'll lose what you've entered."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        destructive
        onCancel={() => setDiscardOpen(false)}
        onConfirm={confirmDiscard}
      />

      <EditTransactionSheet
        transaction={
          editingId
            ? data.transactions.find((t) => t.id === editingId) ?? null
            : null
        }
        onClose={() => setEditingId(null)}
        onUpdate={(id, draft) => {
          actions.updateTransaction(id, {
            type: draft.type,
            description: draft.description,
            categoryId: draft.categoryId,
            date: draft.date,
            amount: draft.amount,
            spreadDays: draft.spreadDays,
          });
          toast.show({ message: 'Transaction updated', kind: 'success' });
        }}
      />
    </main>
  );
}

type HeroViewToggleProps = {
  value: 'daily' | 'period';
  onChange: (next: 'daily' | 'period') => void;
};

function HeroViewToggle({ value, onChange }: HeroViewToggleProps) {
  return (
    <div
      role="tablist"
      className="inline-flex p-0.5 rounded-pill bg-surface-secondary text-[11px] font-medium"
    >
      {(['daily', 'period'] as const).map((v) => {
        const active = v === value;
        return (
          <motion.button
            key={v}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => {
              if (!active) onChange(v);
            }}
            whileTap={tapScale}
            transition={springQuick}
            className={cn(
              'px-2.5 py-0.5 rounded-pill transition-colors',
              active
                ? 'bg-surface text-text-primary shadow-sm'
                : 'text-text-secondary',
            )}
          >
            {v === 'daily' ? 'Daily' : 'Period'}
          </motion.button>
        );
      })}
    </div>
  );
}
